import React, { ErrorInfo, PropsWithChildren } from 'react';

import { Button } from '@instinet-analytics/semantic-ui-components';
import ContactSupportModal from 'app/contactSupport/ContactSupportModal';
import { WidgetType } from 'widgets/WidgetType';
import Services from 'store/Services';
import OverlayPanel from 'utils/components/OverlayPanel/OverlayPanel';

export type ErrorBoundaryProps = PropsWithChildren<{
    componentId: string;
    componentType: WidgetType;
    services: Services;
}>;

export interface ErrorBoundaryState {
    hasError: boolean;
}

/*
 * Error boundary.
 * See https://reactjs.org/docs/error-boundaries.html
 */
export default class ErrorBoundary extends React.Component<
    ErrorBoundaryProps,
    ErrorBoundaryState
> {
    public constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    public static getDerivedStateFromError(_error: Error) {
        return { hasError: true };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        const { loggingService } = this.props.services;
        loggingService.critical(
            'ErrorBoundary',
            `Unhandled Error in ${this.props.componentType}-${this.props.componentId}`,
            { error, errorInfo }
        );
    }

    private reload() {
        this.setState({ hasError: false });
    }

    public render() {
        if (this.state.hasError) {
            return (
                <OverlayPanel className="ErrorBoundary">
                    <div className="content">
                        <div className="title">Something went wrong.</div>

                        <div className="subtitle">
                            Please reload the widget. If the issue persists,
                            contact support.
                        </div>
                        <Button primary onClick={() => this.reload()}>
                            Reload
                        </Button>
                        <ContactSupportModal
                            trigger={<Button primary>Contact Support</Button>}
                        />
                    </div>
                </OverlayPanel>
            );
        }

        return this.props.children;
    }
}
